export interface GroupBuilding { _id: string; name: string; slug?: string; address?: string; city?: string; region?: string }
export interface GroupProperty {
  _id: string; title: string; slug?: string; building_id?: string; reference_code?: string;
  address?: string; city?: string; region?: string; status: string;
}
export interface PropertyCounts { total: number; published: number; draft: number; archived: number }
export interface PropertyPage<T> { page: number; totalPages: number; startIndex: number; items: T[] }
export const normalizeSearch = (value: string): string => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

export function countPropertyGroups(properties: readonly GroupProperty[]): Map<string, PropertyCounts> {
  const groups = new Map<string, PropertyCounts>();
  for (const property of properties) {
    const id = property.building_id || 'standalone';
    const count = groups.get(id) ?? { total: 0, published: 0, draft: 0, archived: 0 };
    count.total++;
    if (property.status === 'published' || property.status === 'draft' || property.status === 'archived') count[property.status]++;
    groups.set(id, count);
  }
  return groups;
}

export function filterProperties<T extends GroupProperty>(properties: readonly T[], buildings: readonly GroupBuilding[], filters: {
  status?: string; buildingId?: string; kind?: string; search?: string;
}): T[] {
  const names = new Map(buildings.map((b) => [b._id, [b.name, b.address, b.city, b.region].filter(Boolean).join(' ')]));
  const terms = normalizeSearch(filters.search ?? '').split(/\s+/).filter(Boolean);
  return properties.filter((p) => {
    if (filters.status && filters.status !== 'all' && p.status !== filters.status) return false;
    if (filters.buildingId && p.building_id !== filters.buildingId) return false;
    if (filters.kind === 'standalone' && p.building_id) return false;
    if (filters.kind === 'with_building' && !p.building_id) return false;
    const text = normalizeSearch([p.title, p.reference_code, p.slug, p.address, p.city, p.region, p.building_id && names.get(p.building_id)].filter(Boolean).join(' '));
    return terms.every((term) => text.includes(term));
  });
}

export function filterBuildings<T extends GroupBuilding>(buildings: readonly T[], properties: readonly GroupProperty[], search: string): T[] {
  const units = new Map<string, string[]>();
  for (const p of properties) {
    if (!p.building_id) continue;
    const text = [p.title, p.reference_code, p.address, p.slug].filter(Boolean).join(' ');
    const values = units.get(p.building_id) ?? [];
    values.push(text); units.set(p.building_id, values);
  }
  const terms = normalizeSearch(search).split(/\s+/).filter(Boolean);
  return buildings.filter((b) => {
    const text = normalizeSearch([b.name, b.slug, b.address, b.city, b.region, ...(units.get(b._id) ?? [])].filter(Boolean).join(' '));
    return terms.every((term) => text.includes(term));
  });
}

export function pageOf<T>(items: readonly T[], requestedPage: number, pageSize = 10): PropertyPage<T> {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const page = Math.min(totalPages, Math.max(1, Number.isFinite(requestedPage) ? Math.floor(requestedPage) : 1));
  const startIndex = (page - 1) * pageSize;
  return { page, totalPages, startIndex, items: items.slice(startIndex, startIndex + pageSize) };
}
